import { defineMessages } from 'react-intl';

export const HEADER_HEIGHT = 350;
export const TITLE_CHAR_LIMIT = 64;
export const DESCRIPTION_CHAR_LIMIT = 4000;
export const SUGGESTIONS_TOTAL = 10;
export const SEARCH_VIEWPORT_PADDING = 20;

export const keys = Object.freeze({
  SUGGESTIONS: 'suggestions',
  DETAILS: 'details',
  STATION_ID: 'stationId',
  SEEDS: 'seeds',
  LAUNCH_ADD_VARIETY: 'launchAddVariety',
  VIDEO_ADS_DISABLED: 'videoAdsDisabled',
} as const);

export const actions = Object.freeze({
  GET_SUGGESTIONS: 'GET_SUGGESTIONS',
  GET_DETAILS: 'GET_DETAILS',
  GET_SEEDS: 'GET_SEEDS',
  ADD_SEED: 'ADD_SEED',
  DELETE_SEED: 'DELETE_SEED',
  RESET_STATION_FEEDBACK_SUCCESS: 'RESET_STATION_FEEDBACK_SUCCESS',
  LAUNCH_ADD_VARIETY: 'LAUNCH_ADD_VARIETY',
  LEAVE_STATION_DETAILS: 'LEAVE_STATION_DETAILS',
  DISABLE_VIDEO_ADS_FOR_STATION: 'DISABLE_VIDEO_ADS_FOR_STATION',
} as const);

export const CollectedStationComponentEventTypes = Object.freeze({
  AAM_TOGGLE_ON: 'AAM_TOGGLE_ON',
  AAM_TOGGLE_OFF: 'AAM_TOGGLE_OFF',
  EDIT_DESCRIPTION: 'EDIT_DESCRIPTION',
  ADD_VARIETY: 'ADD_VARIETY',
  SEE_ALL: 'SEE_ALL',
  SKIP_TO_THUMBS: 'SKIP_TO_THUMBS',
} as const);

export const CollectedStationContentSections = Object.freeze({
  ARTIST_MESSAGING: 'artist_messaging',
  STATION_DESCRIPTION: 'station_description',
  HEADER: 'header',
  LINKED_PLAYLIST: 'linked_playlist',
  STATION_CREATED_FROM: 'station_created_from',
  THUMBS: 'thumbs',
  THUMBS_UP: 'thumbs_up',
  THUMBS_DOWN: 'thumbs_down',
  CONTROL_BAR: 'control_bar',
} as const);

export const messages = defineMessages({
  stationCreatedFrom: {
    id: 't.stationCreatedFrom',
    defaultMessage: 'Station Created From',
  },
  created: {
    id: 't.created',
    defaultMessage: 'Created on {stationDate}',
  },
  sharedCreated: {
    id: 't.sharedCreated',
    defaultMessage: 'Created by {stationOwner} on {stationDate}',
  },
  thumbHistory: {
    id: 't.thumbHistory',
    defaultMessage: 'Thumb History',
  },
  remove: {
    id: 't.remove',
    defaultMessage: 'Remove',
  },
  artistAudioTitle: {
    id: 't.artistAudioTitle',
    defaultMessage: 'Artist Audio Messages',
  },
  artistAudioInformation: {
    id: 't.artistAudioInformation',
    defaultMessage: 'Turn on to get updates from your favorite artists.',
  },
  artistTakeoverModeTitle: {
    id: 't.artistTakeoverModeTitle',
    defaultMessage: 'Exclusive Artist Station Modes',
  },
  artistTakeoverModeSubtext: {
    id: 't.artistTakeoverModeSubtext',
    defaultMessage: 'Station Mode',
  },
  switchOn: {
    id: 't.switchOn',
    defaultMessage: 'On',
  },
  switchOff: {
    id: 't.switchOff',
    defaultMessage: 'Off',
  },
  defaultPlaceholderHeader: {
    id: 't.defaultPlaceholderHeader',
    defaultMessage: 'This is your space to write a description of your station.',
  },
  defaultPlaceholderWhy: {
    id: 't.defaultPlaceholderWhy',
    defaultMessage:
      'Adding a description will place this station in our search directory, and will make it available for other Pandora listeners to find.',
  },
  titleLenError: {
    id: 't.titleLenError',
    defaultMessage: 'You must enter at least one character',
  },
  titleTooLongError: {
    id: 't.titleTooLongError',
    defaultMessage: 'Your title must be less than {maxLength} characters',
  },
  saveModalConfirmation: {
    id: 't.saveModalConfirmation',
    defaultMessage: 'Changes saved!',
  },
  stationDescription: {
    id: 't.stationDescription',
    defaultMessage: 'Station Description',
  },
  addVariety: {
    id: 't.addVariety',
    defaultMessage: 'Add Variety',
  },
  varietyModalSubtitle: {
    id: 't.varietyModalSubtitle',
    defaultMessage: 'Add Artists or Songs to this station.',
  },
  cancel: {
    id: 't.cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 't.save',
    defaultMessage: 'Save',
  },
  searchPlaceholder: {
    id: 't.searchPlaceholder',
    defaultMessage: 'Enter an Artist, Song, or Composer',
  },
  suggestionListTitle: {
    id: 't.suggestionListTitle',
    defaultMessage: 'Stumped? Try one of these',
  },
  delete: {
    id: 't.delete',
    defaultMessage: 'Delete',
  },
  viewMore: {
    id: 't.viewMore',
    defaultMessage: 'View More',
  },
  thumbsUpHeader: {
    id: 't.thumbsUpHeader',
    defaultMessage: 'Thumbed Up Tracks',
  },
  thumbsDownHeader: {
    id: 't.thumbsDownHeader',
    defaultMessage: 'Thumbed Down Tracks',
  },
  seeAllThumbsUp: {
    id: 't.seeAllThumbsUp',
    defaultMessage: 'See All Thumbs Up',
  },
  seeAllThumbsDown: {
    id: 't.seeAllThumbsDown',
    defaultMessage: 'See All Thumbs Down',
  },
  noThumbsDown: {
    id: 't.noThumbDowns',
    defaultMessage: 'Currently no thumbs down in session history.',
  },
  noThumbsUp: {
    id: 't.noThumbDowns',
    defaultMessage: 'Currently no thumbs up in session history.',
  },
  getThumbing: {
    id: 't.getThumbing',
    defaultMessage: 'Get thumbing!',
  },
  thumbprintDescription: {
    id: 't.thumbprintDescription',
    defaultMessage: 'Music inspired by your {thumbInformation} from across all your stations.',
  },
  linkedPlaylistTitle: {
    id: 't.linkedPlaylistTitle',
    defaultMessage: 'Linked Playlist',
  },
  linkedPlaylistSubtext: {
    id: 't.linkedPlaylistSubtext',
    defaultMessage: 'By Pandora',
  },
  linkedPlaylistTrackCount: {
    id: 't.linkedPlaylistTrackCount',
    defaultMessage: '{count} Songs',
  },
  playlist: {
    id: 't.playlist',
    defaultMessage: 'Playlist',
  },
} as const);
